<script lang="ts">
	import LocationInput from "@components/inputs/locationInput/LocationInput.svelte";
	import { useGroup } from "@components/utils/group.store";
	import { locationQuery } from "@stores/searchQuery";
	import { Dialog } from "bits-ui";
	import { onMount } from "svelte";
	import { Drawer } from "vaul-svelte";
	import DrawerContent from "./DrawerContent.svelte";
	import DrawerHandle from "./DrawerHandle.svelte";
	import DrawerHeader from "./DrawerHeader.svelte";

	// Use a reactive variable to track screen size
	let large = $state(false);

	// Function to check if screen is large
	function checkScreenSize() {
		large = window.matchMedia("(min-width: 1024px)").matches;
	}

	// Add event listener for screen resize
	onMount(() => {
		checkScreenSize();
		window.addEventListener("resize", checkScreenSize);

		return () => {
			window.removeEventListener("resize", checkScreenSize);
		};
	});

	const { selected, setValue } = useGroup<boolean>("locationSearchDrawer");

	let drawerTriggerRef: HTMLElement | null = $state(null);
	let dialogTriggerRef: HTMLElement | null = $state(null);
	let drawerCloseRef: HTMLElement | null = $state(null);
	let dialogCloseRef: HTMLElement | null = $state(null);

	function open() {
		if (large) {
			dialogTriggerRef?.click();
		} else {
			drawerTriggerRef?.click();
		}
	}

	function close() {
		if (large) {
			dialogCloseRef?.click();
		} else {
			drawerCloseRef?.click();
		}
	}

	selected.subscribe((value) => {
		if (value) {
			setTimeout(() => {
				open();
			}, 0);
		}
	});

	function handleLocationSelect() {
		close();
	}
</script>

{#snippet content()}
	<DrawerContent>
		<DrawerHandle />
		<DrawerHeader
			title="Locatie instellen"
			onClose={() => {
				close();
			}}
		/>
		<div class="w-full">
			<div class="flex h-10 w-full items-center rounded-lg bg-gray-100">
				<LocationInput
					requestGeolocation={$locationQuery === ""}
					optionsShadow={"shadow-none"}
					on:select={handleLocationSelect}
					borderless
					withOptions={true}
				/>
			</div>
		</div>
	</DrawerContent>
{/snippet}

{#if !large}
	<Drawer.Root
		onClose={() => {
			setTimeout(() => {
				setValue(null);
			}, 200);
		}}
	>
		<Drawer.Trigger class="hidden">
			<div bind:this={drawerTriggerRef}></div>
		</Drawer.Trigger>
		<Drawer.Close class="hidden">
			<div bind:this={drawerCloseRef}></div>
		</Drawer.Close>

		<Drawer.Portal>
			<Drawer.Overlay class="fixed inset-0 z-[60] bg-black/80" />

			<Drawer.Content
				class="fixed bottom-0 left-0 right-0 z-[70] flex h-full max-h-[85vh] flex-1 flex-col rounded-t-[10px] bg-white"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Drawer.Content>
		</Drawer.Portal>
	</Drawer.Root>
{:else}
	<Dialog.Root
		onOpenChange={(open) => {
			if (!open) {
				setValue(null);
			}
		}}
	>
		<Dialog.Trigger class="hidden">
			<div bind:this={dialogTriggerRef}></div>
		</Dialog.Trigger>
		<Dialog.Close class="hidden" bind:ref={dialogCloseRef}></Dialog.Close>
		<Dialog.Portal>
			<Dialog.Overlay class="fixed inset-0 z-[60] bg-black/50 pl-80" />

			<Dialog.Content
				class="fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg xl:left-[calc(50%+var(--spacing)*40)]"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Dialog.Content>
		</Dialog.Portal>
	</Dialog.Root>
{/if}
